import React from "react";
import { Badge, Grid, Paper, Table, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { formatAmount, hasBalanceSheetActivity } from "./financialStatementsUtils";

const amountCellStyle = { textAlign: "right", whiteSpace: "nowrap" };

const SectionHeaderRow = ({ label }) => (
  <tr>
    <td colSpan={3}>
      <Text size="xs" weight={700} transform="uppercase" color="dimmed">
        {label}
      </Text>
    </td>
  </tr>
);

const SubtotalRow = ({ label, amount, bold = false }) => (
  <tr>
    <td colSpan={2}>
      <Text weight={bold ? 700 : 600}>{label}</Text>
    </td>
    <td style={amountCellStyle}>
      <Text weight={bold ? 700 : 600}>{formatAmount(amount)}</Text>
    </td>
  </tr>
);

const AccountRows = ({ rows = [] }) =>
  rows.map((row) => (
    <tr key={row.code}>
      <td>{row.code}</td>
      <td>{row.name}</td>
      <td style={amountCellStyle}>{formatAmount(row.amount)}</td>
    </tr>
  ));

const BalanceSheetView = ({ report, loading = false, periodLabel = "" }) => {
  const { t } = useTranslation();

  if (loading && !report) {
    return (
      <Text size="sm" color="dimmed">
        {t("accounting.financialStatements.loading")}
      </Text>
    );
  }

  if (!report) return null;

  const totalAssets = Number(report.totalAssets ?? 0);
  const totalLiabilities = Number(report.totalLiabilities ?? 0);
  const totalEquity = Number(report.totalEquity ?? 0);
  const totalLiabilitiesAndEquity = Number(report.totalLiabilitiesAndEquity ?? 0);
  const hasActivity = hasBalanceSheetActivity(report);

  return (
    <>
      <Paper withBorder p="md" mb="md">
        <Text size="xs" color="dimmed" mb="sm" transform="uppercase" weight={600}>
          {t("accounting.financialStatements.summaryTitle")}
        </Text>
        <Grid>
          <Grid.Col span={3}>
            <Text size="xs" color="dimmed">
              {t("accounting.financialStatements.totalAssets")}
            </Text>
            <Text weight={700} size="lg">
              {formatAmount(totalAssets)}
            </Text>
          </Grid.Col>
          <Grid.Col span={3}>
            <Text size="xs" color="dimmed">
              {t("accounting.financialStatements.totalLiabilities")}
            </Text>
            <Text weight={700} size="lg">
              {formatAmount(totalLiabilities)}
            </Text>
          </Grid.Col>
          <Grid.Col span={3}>
            <Text size="xs" color="dimmed">
              {t("accounting.financialStatements.totalEquity")}
            </Text>
            <Text weight={700} size="lg">
              {formatAmount(totalEquity)}
            </Text>
          </Grid.Col>
          <Grid.Col span={3}>
            <Text size="xs" color="dimmed">
              {t("Status")}
            </Text>
            <Badge
              mt={4}
              size="lg"
              color={report.isBalanced ? "green" : "red"}
            >
              {report.isBalanced
                ? t("accounting.trialBalance.balanced")
                : t("accounting.trialBalance.outOfBalance")}
            </Badge>
          </Grid.Col>
        </Grid>
      </Paper>

      <Paper withBorder p="md">
        <Text weight={700} size="lg" mb={4}>
          {t("accounting.financialStatements.bsTitle")}
        </Text>
        {periodLabel && (
          <Text size="sm" color="dimmed" mb="md">
            {periodLabel}
          </Text>
        )}

        {!hasActivity ? (
          <Text size="sm" color="dimmed">
            {t("accounting.financialStatements.noActivity")}
          </Text>
        ) : (
          <Table striped highlightOnHover>
            <thead>
              <tr>
                <th>{t("Code")}</th>
                <th>{t("accounting.journal.account")}</th>
                <th style={amountCellStyle}>{t("Amount")}</th>
              </tr>
            </thead>
            <tbody>
              <SectionHeaderRow label={t("accounting.financialStatements.assets")} />
              <AccountRows rows={report.assets} />
              <SubtotalRow
                label={t("accounting.financialStatements.totalAssets")}
                amount={totalAssets}
              />

              <SectionHeaderRow
                label={t("accounting.financialStatements.liabilities")}
              />
              <AccountRows rows={report.liabilities} />
              <SubtotalRow
                label={t("accounting.financialStatements.totalLiabilities")}
                amount={totalLiabilities}
              />

              <SectionHeaderRow label={t("accounting.financialStatements.equity")} />
              <AccountRows rows={report.equity} />
              <SubtotalRow
                label={t("accounting.financialStatements.totalEquity")}
                amount={totalEquity}
              />

              <SubtotalRow
                label={t("accounting.financialStatements.totalLiabilitiesAndEquity")}
                amount={totalLiabilitiesAndEquity}
                bold
              />
            </tbody>
          </Table>
        )}
      </Paper>
    </>
  );
};

export default BalanceSheetView;
